// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import IconShipping from 'components/atoms/icons/IconShipping';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import { useEffect, useState, useRef } from 'react';
import { useQuery, useUpdateQuery } from 'util/queryParams';
import { getApiPath, mergeClasses } from 'util/commons';
import {
  CollectRoute,
  RouteCategoryItemType,
} from 'types/routeManagementTypes';
import { getCollectRoutes, getRouteCategories } from 'apis/collect_route';
import _debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { RoleType } from 'util/Enums';
import { Account } from 'util/accountManagementTypes';
import { getUsers } from 'apis/operator_site/siteApi';
import { SelectField } from 'components/molecules/SelectField';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router';
import { Path } from 'routes';

type RouteList = {
  id: number;
  siteNumber: number;
  routeName: string;
  groupId: number;
};

const RouteList = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const query = useQuery();
  const updateQuery = useUpdateQuery();
  const history = useHistory();

  const [routeCategories, setRouteCategories] = useState<
    RouteCategoryItemType[]
  >([]);
  const [drivers, setDrivers] = useState<Account[]>([]);
  const [routes, setRoutes] = useState<CollectRoute[]>([]);
  const [apiCallState, setApiCallState] = useState<
    'drivers' | 'categories' | 'routes'
  >('drivers');
  const [showPage, setShowPage] = useState(false);

  const [keyword, setKeyword] = useState<string>(
    query.get('routeKeyword') || ''
  );
  const [keywordInput, setKeywordInput] = useState<string>(
    query.get('routeKeyword') || ''
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(query.get('category') || undefined);
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(
    query.get('driverId') || undefined
  );

  const routeGroupRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleGetRoutes = () =>
    getCollectRoutes({
      params: {
        category: selectedCategoryId,
        keyword: keyword.trim(),
        driverId: selectedDriverId,
        screen: 'list',
      },
      successCallback: (routes) => {
        if (
          !routes.length &&
          !selectedCategoryId &&
          !keyword.trim() &&
          !selectedDriverId
        ) {
          history.replace(
            getApiPath(Path.routeDetail, {
              routeId: 'all',
            })
          );

          return;
        }

        setRoutes(routes);
      },
      failedCallback: () => setRoutes([]),
      finallyCallback: () => setShowPage(true),
    });

  const updateURLParams = (updates: Record<string, string | undefined>) => {
    updateQuery({
      callback: (searchParams) => {
        Object.entries(updates).forEach(([key, value]) => {
          if (value && value.trim()) {
            searchParams.set(key, value);
          } else {
            searchParams.delete(key);
          }
        });
      },
      replace: true,
    });
  };

  useEffect(() => {
    if (apiCallState !== 'drivers') {
      return;
    }

    if (user.role !== RoleType.UserNormal) {
      getUsers({
        params: {
          take: Number.MAX_SAFE_INTEGER,
        },
        successCallback: (data) => {
          if (
            selectedDriverId &&
            !data.users.some((d) => d.id.toString() === selectedDriverId)
          ) {
            setSelectedDriverId(undefined);
          }

          setDrivers(data.users.filter((u) => u.type === RoleType.UserNormal));
        },
        finallyCallback: () => {
          setApiCallState('categories');
        },
      });
    } else {
      setApiCallState('categories');
    }
  }, [user]);

  useEffect(() => {
    if (apiCallState !== 'categories') return;

    getRouteCategories({
      hasRoutes:
        user.role !== RoleType.UserNormal ? !selectedDriverId : undefined,
      driverId:
        user.role !== RoleType.UserNormal ? selectedDriverId : undefined,
      successCallback: (data) => {
        if (
          selectedCategoryId &&
          !data.find((rc) => rc.id.toString() === selectedCategoryId)
        ) {
          setSelectedCategoryId(undefined);
        }

        setRouteCategories(data);
      },
      finallyCallback: () => {
        setApiCallState('routes');
      },
    });
  }, [user, selectedDriverId, apiCallState]);

  useEffect(() => {
    if (apiCallState !== 'routes') return;

    handleGetRoutes();
  }, [selectedCategoryId, keyword, apiCallState]);

  useEffect(() => {
    updateURLParams({
      category: selectedCategoryId,
      routeKeyword: keyword.trim(),
      driverId: selectedDriverId,
    });
  }, [selectedCategoryId, keyword, selectedDriverId]);

  useEffect(() => {
    const debouncedSetKeyword = _debounce(() => {
      setKeyword(keywordInput);
    }, 500);

    if (keywordInput.trim() !== keyword.trim()) {
      debouncedSetKeyword();
    }

    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [keywordInput]);

  // Scroll to selected category when it changes
  useEffect(() => {
    const categoryKey = selectedCategoryId || 'all';
    const selectedElement = categoryRefs.current[categoryKey];
    const container = routeGroupRef.current;

    if (selectedElement && container) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();

      // Check if element is outside visible area (horizontal scroll)
      if (
        elementRect.left < containerRect.left ||
        elementRect.right > containerRect.right
      ) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [routeCategories, selectedCategoryId]);

  if (!showPage) {
    return null;
  }

  return (
    <div className={styles.routeListWrapper}>
      <div className={`${styles.collectReceived} ${styles.scrollContents}`}>
        <div className={styles.filterWrapper}>
          <div className={styles.selectRouteTitle}>{t('route_list.title')}</div>

          <div className={styles.actionBox}>
            {user.role !== RoleType.UserNormal && (
              <div className={styles.filterBox}>
                <Form onSubmit={() => {}}>
                  {() => (
                    <SelectField
                      options={drivers.map((driver) => ({
                        label: `${driver.name} (${driver.id})`,
                        value: driver.id,
                      }))}
                      name="drivers"
                      searchable
                      placeholder={t('route_list.filter_placeholder') ?? ''}
                      value={selectedDriverId}
                      onChange={(value) => {
                        setSelectedDriverId(value?.toString());
                        setApiCallState('categories');
                      }}
                      isDarkmode
                      allowClear
                      showPlaceholderOption
                    />
                  )}
                </Form>
              </div>
            )}

            <div className={styles.searchBox}>
              <IconSearch size={24} />
              <input
                placeholder={t('route_list.search_placeholder') || ''}
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        {routeCategories.length > 0 && (
          <div className={styles.routeGroup} ref={routeGroupRef}>
            <div
              ref={(el) => {
                categoryRefs.current['all'] = el;
              }}
              className={mergeClasses(styles.routeGroupItem, {
                [styles.active]: !selectedCategoryId,
              })}
              onClick={() => setSelectedCategoryId(undefined)}
            >
              {t('route_list.categories.all')}
            </div>

            {routeCategories.map((item) => {
              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    categoryRefs.current[item.id.toString()] = el;
                  }}
                  className={mergeClasses(styles.routeGroupItem, {
                    [styles.active]: item.id.toString() === selectedCategoryId,
                  })}
                  onClick={() => setSelectedCategoryId(item.id.toString())}
                >
                  {item.name}
                </div>
              );
            })}
          </div>
        )}

        {routes.length > 0 ? (
          <div className={styles.routeList}>
            {routes.map((route) => (
              <div
                key={route.id}
                className={styles.routeCard}
                onClick={() => {
                  history.push(
                    `${getApiPath(Path.routeDetail, {
                      routeId: route.id.toString(),
                    })}${window.location.search}`
                  );
                }}
              >
                <div className={styles.routeCardTop}>
                  <IconShipping strokeColor="#489933" size={40} />
                  <div className={styles.siteNumber}>{`${
                    route.emitSitesCount || 0
                  } ${t('route_list.sites')}`}</div>
                </div>

                <div className={styles.routeName}>{route.routeName}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.noData}>{t('messages.M_008')}</div>
        )}
      </div>
    </div>
  );
};

export default RouteList;
